// Services
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import InventoryService from "services/inventory-items-service";
import LocationsService from "services/locations-service";
import partCodesService from "services/part-code-service";

// MUI and Material Dashboard components
import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Slide from "@mui/material/Slide";

import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

// This page's components
import Table from "./Table";
import PartCodeDetails from "../components/partcode-details";
import { formatIdentifier } from "../components/helperFunctions";

export default function SpecificItemView() {

    const { id } = useParams();
    const [inventoryItem, setInventoryItem] = useState({}); // Stores the fetched inventory item's details
    const [selectedPartCode, setSelectedPartCode] = useState(null); // Stores inventory item's partcode 
    const [showPartCodeDetails, setShowPartCodeDetails] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [imageUrl, setImageUrl] = useState(""); // Stores the location image's URL
    const [totalQuantity, setTotalQuantity] = useState(0); // Stores the total quantity of the inventory item accross all locations
    const [title, setTitle] = useState(""); // Stores the title of the page
    const [reservations, setReservations] = useState([]); // Stores the inventory item's reservations

    const navigate = useNavigate();
    // Fetches the inventory item from the API 
    useEffect(() => {
        (async () => {
            try {
                const inventoryItemData = await fetchItem();
                await fetchLocationImageUrl(inventoryItemData);
            } catch (error) {
                console.error("Error fetching inventory item:", error);
                // If the inventory item is not found, redirect to the inventory view
                navigate("/inventory-management");
            }
        })();
    }, []);

    useEffect(() => {
        if (imageUrl) {
            setShowMap(true);
        }
    }, [imageUrl]);

    const fetchItem = async () => {
        try {
            const response = await InventoryService.getOneInventoryItem(id);
            const inventoryItemData = response.data?.attributes ?? {};
            let totalQuantityResponse;
            if (inventoryItemData.partCode && inventoryItemData.partCode._id) {
                totalQuantityResponse = await InventoryService.getTotalQuantityByModel(inventoryItemData.partCode._id);
            } else {
                totalQuantityResponse = { data: { attributes: { totalQuantity: 0 } } };
            }
            const reservationsResponse = await InventoryService.getItemReservations(id);

            setInventoryItem(inventoryItemData);
            setSelectedPartCode(inventoryItemData.partCode);
            setShowPartCodeDetails(true);
            setTotalQuantity(totalQuantityResponse.data?.attributes?.totalQuantity ?? 0);
            setTitle(formatIdentifier(inventoryItemData).replace(/-/g, "–"));
            setReservations(reservationsResponse.data ?? []);

            return inventoryItemData;
        } catch (error) {
            console.error("Error fetching inventory item or total quantity:", error);
            throw error;
        }
    };

    const fetchLocationImageUrl = async (inventoryItemData) => {
        try {
            if (!inventoryItemData) return;

            if (inventoryItemData.locationId?.imagePath) {
                const imageURL = await LocationsService.getLocationImage(
                    encodeURIComponent(inventoryItemData.locationId.imagePath)
                );
                setImageUrl(imageURL);
            } else if (inventoryItemData.city === "Smith Falls" && parseRackNum(inventoryItemData.rack) <= 30) {
                const s3Key = `Locations/${inventoryItemData.city}/${inventoryItemData.rack}.png`;
                const image = await partCodesService.getImage(s3Key);
                if (image) setImageUrl(image);
            } else if (
                inventoryItemData.city === "Kanata" &&
                parseRackNum(inventoryItemData.rack) <= 6 &&
                inventoryItemData.location === "L2"
            ) {
                const s3Key = `Locations/${inventoryItemData.city}/${inventoryItemData.location}${inventoryItemData.rack}.png`;
                const image = await partCodesService.getImage(s3Key);
                if (image) setImageUrl(image);
            }
        } catch (error) {
            console.error("Error fetching location image:", error);
        }
    };

    const parseRackNum = (rack) => {
        return parseInt(rack.slice(1));
    }

    return (
        <MDBox>
            <DashboardLayout>
                <DashboardNavbar breadcrumbTitle={title} />
                <MDBox mt={5} mb={9}>

                    <Grid containter justifyContent="center" spacing={1}>

                        {/* Title */}
                        <Grid item xs={12} lg={12}>
                            <MDBox
                                mt={6}
                                mb={4}
                                textAlign="center"
                            >
                                <MDBox mb={1}>
                                    <MDTypography variant="h4" fontWeight="bold">
                                        Inventory Item Details
                                    </MDTypography>
                                </MDBox>
                                <MDTypography variant="h6" fontWeight="bold">
                                    {formatIdentifier(inventoryItem)}
                                </MDTypography>
                            </MDBox>
                        </Grid>

                    </Grid>
                    <Grid container justifyContent="center" spacing={2}>

                        <Slide direction="up" in={showPartCodeDetails} mountOnEnter unmountOnExit timeout={1000}>
                            <Grid item xs={12} lg={showMap ? 7 : 8}>

                                {/* Part Code Details */}
                                <PartCodeDetails id={selectedPartCode ? selectedPartCode._id : ""} />

                                <MDBox mt={2}>
                                    <Table
                                        InventoryItem={inventoryItem}
                                        totalQuantity={totalQuantity}
                                        reservations={reservations}
                                        refreshItemData={fetchItem}
                                    />
                                </MDBox>

                            </Grid>
                        </Slide>

                        {/* Inventory Item Details */}
                        {showMap && (
                            <Grid item xs={12} lg={5}>
                                <MDBox sx={{ position: { lg: "sticky" }, top: { lg: "96px" }, alignSelf: "flex-start" }}>
                                    <Card>
                                        <MDBox p={2}>

                                            {/* Title */}
                                            <MDTypography variant="h4" fontWeight="bold" m={2}>
                                                Location
                                            </MDTypography>

                                            {/* Location Map */}
                                            <PhotoProvider>
                                                <PhotoView src={imageUrl}>
                                                    <img src={imageUrl} alt="" width="100%" />
                                                </PhotoView>
                                            </PhotoProvider>

                                        </MDBox>
                                    </Card>
                                </MDBox>
                            </Grid>
                        )}

                    </Grid>
                </MDBox>
                <Footer />

            </DashboardLayout>

        </MDBox>
    )
}